import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthAdminService, GrowthTenantConfigResponse } from './growth-admin.service';
import { GROWTH_MARKETING_CHANNEL_KEYS, buildGrowthMarketingChannelOptions } from './growth-channel.constants';

interface GuardrailPolicyForm {
  makerCheckerStrict: boolean;
  allowLaunchWithoutSimulation: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-tenant-config',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    ButtonModule
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.tenantConfig.title')"
      [description]="t('growth.tenantConfig.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('growth.tenantConfig.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('growth.tenantConfig.errorTitle')" [message]="error() ?? ''" />
      } @else {
        <section class="form-grid">
          <label>
            <span>{{ t('growth.tenantConfig.defaultIndustryKey') }}</span>
            <p-select
              [options]="industryOptions"
              optionValue="value"
              [(ngModel)]="model.defaultIndustryKey"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('growth.tenantConfig.defaultAutomationLevel') }}</span>
            <p-select
              [options]="automationOptions"
              optionValue="value"
              [(ngModel)]="model.defaultAutomationLevel"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('growth.tenantConfig.allowedChannels') }}</span>
            <p-multiSelect
              [options]="channelOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="allowedChannels"
              [showClear]="true"
              appendTo="body"
              [placeholder]="t('growth.tenantConfig.allowedChannelsPlaceholder')" />
          </label>
          <div class="guardrail-section">
            <div class="section-title">
              <span>{{ t('growth.tenantConfig.guardrailPolicy') }}</span>
              <ain-info-hint
                [titleKey]="'growth.tenantConfig.guardrailPolicyHint.title'"
                [descriptionKey]="'growth.tenantConfig.guardrailPolicyHint.description'" />
            </div>
            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="guardrailForm.makerCheckerStrict"></p-checkbox>
              <span>{{ t('growth.tenantConfig.guardrail.makerCheckerStrict') }}</span>
            </label>
            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="guardrailForm.allowLaunchWithoutSimulation"></p-checkbox>
              <span>{{ t('growth.tenantConfig.guardrail.allowLaunchWithoutSimulation') }}</span>
            </label>
          </div>
          <label class="checkbox-row">
            <p-checkbox binary="true" [(ngModel)]="model.isEnabled"></p-checkbox>
            <span>{{ t('growth.tenantConfig.isEnabled') }}</span>
          </label>
        </section>
        <div class="actions">
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>
      }
    </ain-page>
  `,
  styles: [`
    .form-grid { display: grid; gap: 12px; max-width: 760px; }
    .form-grid label { display: grid; gap: 6px; }
    .checkbox-row { display: flex !important; align-items: center; gap: 8px; }
    .guardrail-section { display: grid; gap: 8px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .actions { margin-top: 12px; }
  `]
})
export class GrowthTenantConfigComponent implements OnInit {
  private readonly growthAdminService = inject(GrowthAdminService);
  private readonly localizationService = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  model: GrowthTenantConfigResponse = {
    tenantId: '',
    defaultIndustryKey: 'retail',
    defaultAutomationLevel: 'HumanApproval',
    allowedChannelsJson: '[]',
    isEnabled: true,
    guardrailPolicyJson: '{}'
  };

  allowedChannels: string[] = [];
  guardrailForm: GuardrailPolicyForm = {
    makerCheckerStrict: false,
    allowLaunchWithoutSimulation: true
  };

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  readonly industryOptions = [
    { value: 'retail', labelKey: 'growth.industry.retail' },
    { value: 'banking', labelKey: 'growth.industry.banking' }
  ];

  readonly automationOptions = [
    { value: 'HumanApproval', labelKey: 'growth.automation.humanApproval' },
    { value: 'Assisted', labelKey: 'growth.automation.assisted' },
    { value: 'AutoInLimit', labelKey: 'growth.automation.autoInLimit' }
  ];

  ngOnInit(): void {
    this.growthAdminService.getTenantConfig().subscribe({
      next: config => {
        this.model = config;
        this.allowedChannels = this.parseAllowedChannels(config.allowedChannelsJson);
        this.guardrailForm = this.parseGuardrailPolicy(config.guardrailPolicyJson);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.tenantConfig.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.saving.set(true);
    this.growthAdminService.updateTenantConfig({
      defaultIndustryKey: this.model.defaultIndustryKey,
      defaultAutomationLevel: this.model.defaultAutomationLevel,
      allowedChannelsJson: JSON.stringify(this.allowedChannels),
      isEnabled: this.model.isEnabled,
      guardrailPolicyJson: JSON.stringify(this.guardrailForm)
    }).subscribe({
      next: response => {
        this.model = response;
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.tenantConfig.errorSave'));
        this.saving.set(false);
      }
    });
  }

  private parseAllowedChannels(json: string): string[] {
    try {
      return JSON.parse(json) as string[];
    } catch {
      return [];
    }
  }

  private parseGuardrailPolicy(json: string): GuardrailPolicyForm {
    try {
      const parsed = JSON.parse(json) as Partial<GuardrailPolicyForm>;
      return {
        makerCheckerStrict: parsed.makerCheckerStrict ?? false,
        allowLaunchWithoutSimulation: parsed.allowLaunchWithoutSimulation ?? true
      };
    } catch {
      return { makerCheckerStrict: false, allowLaunchWithoutSimulation: true };
    }
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
